package com.anjuros.website.redesign.sections.portal

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdBreadcrumb
import com.anjuros.website.redesign.components.RdGhostButton
import com.anjuros.website.redesign.components.RdLede
import com.anjuros.website.redesign.components.RdPicture
import com.anjuros.website.redesign.components.RdPrimaryButton
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.PortalEventActiveStyle
import com.anjuros.website.redesign.styles.PortalEventBottomRightStyle
import com.anjuros.website.redesign.styles.PortalEventIconStyle
import com.anjuros.website.redesign.styles.PortalEventLowerLeftStyle
import com.anjuros.website.redesign.styles.PortalEventStyle
import com.anjuros.website.redesign.styles.PortalEventTopLeftStyle
import com.anjuros.website.redesign.styles.PortalEventUpperRightStyle
import com.anjuros.website.redesign.styles.PortalFreeStyle
import com.anjuros.website.redesign.styles.PortalGlowStyle
import com.anjuros.website.redesign.styles.PortalHeroClipStyle
import com.anjuros.website.redesign.styles.PortalHeroGridStyle
import com.anjuros.website.redesign.styles.PortalLivePhoneStyle
import com.anjuros.website.redesign.styles.PortalLiveStyle
import com.anjuros.website.redesign.styles.PortalStatusStyle
import com.anjuros.website.redesign.styles.RdButtonRowStyle
import com.anjuros.website.redesign.styles.RdHeroCopyStyle
import com.anjuros.website.redesign.styles.RdHeroStyle
import com.anjuros.website.redesign.styles.RdMarkStyle
import com.anjuros.website.redesign.styles.RdSubpageTitleStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One job update floating around the phone; [position] places it on wider screens. */
private class JobEvent(val icon: @Composable () -> Unit, val title: RdText, val text: RdText, val position: Modifier)

@Composable
fun PortalHeroSection() {
    RdSection(labelledBy = "h1", modifier = RdHeroStyle.toModifier().then(PortalHeroClipStyle.toModifier()), wrapModifier = PortalHeroGridStyle.toModifier()) {
        Div(RdHeroCopyStyle.toModifier().toAttrs()) {
            RdBreadcrumb(RdText.PORTAL_CRUMB)
            H1(RdSubpageTitleStyle.toModifier().id("h1").toAttrs()) {
                Text(LocalText(RdText.PORTAL_HERO_TITLE_START))
                Span(RdMarkStyle.toModifier().toAttrs()) { Text(LocalText(RdText.PORTAL_HERO_TITLE_MARK)) }
            }
            RdLede(RdText.PORTAL_HERO_LEDE)
            Div(RdButtonRowStyle.toModifier().toAttrs()) {
                RdPrimaryButton("#journey", RdText.PORTAL_HERO_HOW, withArrow = true)
                RdGhostButton(RdRoute.CONTACT, RdText.NAV_GET_STARTED)
            }
            P(PortalFreeStyle.toModifier().toAttrs()) {
                B { Text(LocalText(RdText.PORTAL_FREE_BOLD)) }
                Text(LocalText(RdText.PORTAL_FREE_TEXT))
            }
        }
        LivePortal()
    }
}

/** The portal on a phone, with a job's updates lighting up in turn: new, on the way, paid, reviewed. */
@Composable
private fun LivePortal() {
    val events = listOf(
        JobEvent({ RdIcons.Mail(size = 18) }, RdText.PORTAL_EVENT1_TITLE, RdText.PORTAL_EVENT1_TEXT, PortalEventTopLeftStyle.toModifier()),
        JobEvent({ RdIcons.Car(size = 18) }, RdText.PORTAL_EVENT2_TITLE, RdText.PORTAL_EVENT2_TEXT, PortalEventUpperRightStyle.toModifier()),
        JobEvent({ RdIcons.Pound(size = 18) }, RdText.PORTAL_EVENT3_TITLE, RdText.PORTAL_EVENT3_TEXT, PortalEventLowerLeftStyle.toModifier()),
        JobEvent({ RdIcons.Star(size = 18) }, RdText.PORTAL_EVENT4_TITLE, RdText.PORTAL_EVENT4_TEXT, PortalEventBottomRightStyle.toModifier()),
    )
    val statuses = rdList(LocalText(RdText.PORTAL_EVENT_STATUSES))
    var active by remember { mutableStateOf(0) }

    LaunchedEffect(Unit) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return@LaunchedEffect
        while (true) {
            delay(2200)
            active = (active + 1) % events.size
        }
    }

    Div(PortalLiveStyle.toModifier().toAttrs()) {
        Span(PortalGlowStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs())
        Span(PortalStatusStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) { Text(statuses[active]) }
        RdPicture(RdImage.PORTAL_PHONE_ENQUIRY, LocalText(RdText.PORTAL_PHONE_ALT), lazy = false, modifier = PortalLivePhoneStyle.toModifier())
        events.forEachIndexed { index, event ->
            Div(PortalEventStyle.toModifier().then(event.position).thenIf(index == active, PortalEventActiveStyle.toModifier()).toAttrs()) {
                Span(PortalEventIconStyle.toModifier().toAttrs()) { event.icon() }
                B { Text(LocalText(event.title)) }
                Span { Text(LocalText(event.text)) }
            }
        }
    }
}
